import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Tv, Home, Heart, Clock, List, Settings, LogOut, Shield, 
  ChevronLeft, ChevronRight, Search, User, Menu, X, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navItems = [
  { path: '/', icon: Home, label: 'Dashboard' },
  { path: '/favorites', icon: Heart, label: 'Favorites' },
  { path: '/history', icon: Clock, label: 'Watch History' },
  { path: '/categories', icon: List, label: 'Categories' },
  { path: '/epg', icon: Tv, label: 'TV Guide' },
];

const adminItems = [
  { path: '/admin', icon: Shield, label: 'Admin Panel' },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[rgb(5,5,5)] flex">
      {/* Sidebar - Desktop */}
      <aside 
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-full z-40 glass transition-all duration-300 ${
          collapsed ? 'w-[72px]' : 'w-[260px]'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 p-4 border-b border-white/5">
          <img src="/logo.png" alt="FTA Xtream" className={collapsed ? "w-8 h-8 object-contain" : "h-8 w-auto object-contain"} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                  isActive 
                    ? 'bg-[rgb(124,58,237)] text-white' 
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}

          {/* Admin Section */}
          {isAdmin && (
            <>
              <div className={`pt-4 ${collapsed ? '' : 'px-3'}`}>
                {!collapsed && (
                  <span className="text-xs uppercase tracking-wider text-white/30">Admin</span>
                )}
              </div>
              {adminItems.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-[rgb(16,185,129)] text-white' 
                        : 'text-white/60 hover:bg-white/5 hover:text-white'
                    }`}
                    data-testid={`nav-admin`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span className="font-medium">{item.label}</span>}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-[rgb(31,31,34)] border border-white/10 flex items-center justify-center hover:bg-[rgb(124,58,237)] transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>

        {/* User Section */}
        <div className="p-4 border-t border-white/5">
          {collapsed ? (
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center p-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="logout-btn-collapsed"
            >
              <LogOut className="w-5 h-5" />
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[rgb(31,31,34)] flex items-center justify-center">
                <User className="w-5 h-5 text-white/60" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.username}</p>
                <p className="text-xs text-white/40 truncate flex items-center gap-1">
                  {isAdmin && <Crown className="w-3 h-3 text-[rgb(245,158,11)]" />}
                  {user?.subscription_type !== 'none' ? user?.subscription_type : 'Free'} 
                  {user?.credits > 0 && ` • ${user?.credits?.toFixed(1)} credits`}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-white/5"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[rgb(124,58,237)] flex items-center justify-center">
                <Tv className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold">StreamHub</span>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                <User className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[rgb(15,15,16)] border-white/10">
              <div className="px-3 py-2">
                <p className="font-medium">{user?.username}</p>
                <p className="text-xs text-white/40">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-400 cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[280px] glass-card animate-in slide-in-from-left">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[rgb(124,58,237)] flex items-center justify-center">
                  <Tv className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">StreamHub</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-white/5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="py-4 px-3 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-[rgb(124,58,237)] text-white' 
                        : 'text-white/60 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}

              {isAdmin && (
                <>
                  <div className="pt-4 px-3">
                    <span className="text-xs uppercase tracking-wider text-white/30">Admin</span>
                  </div>
                  {adminItems.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                          isActive 
                            ? 'bg-[rgb(16,185,129)] text-white' 
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 min-h-screen pt-16 lg:pt-0 ${collapsed ? 'lg:pl-[72px]' : 'lg:pl-[260px]'} transition-all duration-300`}>
        {/* Top Bar */}
        <div className="hidden lg:flex items-center justify-between p-6 border-b border-white/5">
          <form onSubmit={handleSearch} className="relative w-96">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <Input
              type="text"
              placeholder="Search channels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-[rgb(31,31,34)] border-[rgb(39,39,42)] text-white placeholder:text-white/30 rounded-full focus:border-[rgb(124,58,237)]"
              data-testid="search-input"
            />
          </form>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium">{user?.username}</p>
              <p className="text-xs text-white/40 flex items-center justify-end gap-1">
                {isAdmin && <Crown className="w-3 h-3 text-[rgb(245,158,11)]" />}
                {user?.subscription_type !== 'none' ? user?.subscription_type : 'Free'}
                {user?.credits > 0 && ` • ${user?.credits?.toFixed(1)} credits`}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-10 h-10 rounded-full bg-[rgb(31,31,34)] flex items-center justify-center hover:bg-white/10 transition-colors" data-testid="user-dropdown">
                  <User className="w-5 h-5 text-white/60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[rgb(15,15,16)] border-white/10">
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={handleLogout} className="text-red-400 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
